import { create } from 'zustand';
import apiClient from '../lib/api';

/** A row of people.shift_types. Times are 'HH:MM'; hours wrap past midnight. */
export interface ShiftType {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  color: string;
  hours: number;
  /** Assignments on or after today — a type in use cannot be deleted. */
  upcomingAssignments: number;
}

/** One employee on one day (people.shift_assignments). `day` is 'YYYY-MM-DD'. */
export interface ShiftAssignment {
  id: string;
  employeeId: string;
  employeeName: string;
  day: string;
  shiftTypeId: string | null;
  shiftName: string;
  hours: number;
  color: string | null;
  startTime: string | null;
  endTime: string | null;
}

export interface ShiftSwap {
  id: string;
  status: string;
  day: string;
  shift: string;
  fromName: string;
  toName: string;
  reason: string | null;
}

export interface ShiftTypeInput { name: string; startTime: string; endTime: string; color?: string }

const normalizeType = (r: any): ShiftType => ({
  id: r.id,
  name: r.name,
  startTime: r.start_time ?? r.startTime ?? '',
  endTime: r.end_time ?? r.endTime ?? '',
  color: r.color || '#94A3B8',
  hours: Number(r.hours) || 0,
  upcomingAssignments: Number(r.upcoming_assignments ?? 0),
});

const normalizeAssignment = (r: any): ShiftAssignment => ({
  id: r.id,
  employeeId: r.employee_id ?? r.employee,
  employeeName: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
  day: String(r.day).slice(0, 10),
  shiftTypeId: r.shift_type_id ?? null,
  shiftName: r.shift_name ?? r.shift ?? '',
  hours: Number(r.hours) || 0,
  color: r.color ?? null,
  startTime: r.start_time ?? null,
  endTime: r.end_time ?? null,
});

const normalizeSwap = (r: any): ShiftSwap => ({
  id: r.id,
  status: r.status,
  day: r.day,
  shift: r.shift,
  fromName: `${r.from_first_name || ''} ${r.from_last_name || ''}`.trim(),
  toName: `${r.to_first_name || ''} ${r.to_last_name || ''}`.trim(),
  reason: r.reason ?? null,
});

const messageOf = (error: any, fallback: string) => error?.response?.data?.message || fallback;

interface ShiftState {
  shifts: ShiftType[];
  assignments: ShiftAssignment[];
  swaps: ShiftSwap[];
  /** The window `assignments` currently holds, so writes can refresh it. */
  range: { from: string; to: string } | null;
  isLoading: boolean;
  error: string | null;

  fetchShifts: () => Promise<void>;
  fetchAssignments: (from: string, to: string) => Promise<void>;
  fetchSwaps: () => Promise<void>;
  /** Set one day; `shiftTypeId: null` clears it. */
  assignShift: (employeeId: string, day: string, shiftTypeId: string | null) => Promise<void>;
  /** Set (or clear, with null) every employee × day pair in one request. Returns rows changed. */
  bulkAssign: (employeeIds: string[], days: string[], shiftTypeId: string | null) => Promise<number>;
  /** Email + in-app notify every employee rostered in [from,to] their schedule. */
  notifyRoster: (from: string, to: string) => Promise<{ notified: number; emailed: number; employees: number }>;
  createShift: (data: ShiftTypeInput) => Promise<void>;
  updateShift: (id: string, data: Partial<ShiftTypeInput>) => Promise<void>;
  deleteShift: (id: string) => Promise<void>;
}

export const useShiftStore = create<ShiftState>((set, get) => {
  /** Reload the visible window and the type usage counts after a write. */
  const refresh = async () => {
    const r = get().range;
    await Promise.all([get().fetchShifts(), r ? get().fetchAssignments(r.from, r.to) : Promise.resolve()]);
  };

  return {
    shifts: [],
    assignments: [],
    swaps: [],
    range: null,
    isLoading: false,
    error: null,

    fetchShifts: async () => {
      try {
        const response = await apiClient.get('/people/shifts');
        set({ shifts: (response.data?.data ?? []).map(normalizeType) });
      } catch (error: any) {
        set({ error: messageOf(error, 'Failed to load shift types') });
      }
    },

    fetchAssignments: async (from, to) => {
      set({ isLoading: true, error: null, range: { from, to } });
      try {
        const response = await apiClient.get('/people/shifts/assignments', { params: { from, to } });
        // Ignore a slow response for a week the user has already navigated away from.
        const r = get().range;
        if (r && (r.from !== from || r.to !== to)) return;
        set({ assignments: (response.data?.data ?? []).map(normalizeAssignment), isLoading: false });
      } catch (error: any) {
        set({ error: messageOf(error, 'Failed to load the roster'), isLoading: false });
      }
    },

    fetchSwaps: async () => {
      try {
        const response = await apiClient.get('/people/shifts/swaps');
        set({ swaps: (response.data?.data ?? []).map(normalizeSwap) });
      } catch {
        set({ swaps: [] });
      }
    },

    assignShift: async (employeeId, day, shiftTypeId) => {
      try {
        await apiClient.post('/people/shifts/assignments', { employeeId, day, shiftTypeId });
      } catch (error: any) {
        throw new Error(messageOf(error, 'Could not update the shift'));
      }
      await refresh();
    },

    bulkAssign: async (employeeIds, days, shiftTypeId) => {
      let count = 0;
      try {
        const response = await apiClient.post('/people/shifts/assignments/bulk', { employeeIds, days, shiftTypeId });
        count = Number(response.data?.data?.count ?? 0);
      } catch (error: any) {
        throw new Error(messageOf(error, 'Could not update the roster'));
      }
      await refresh();
      return count;
    },

    notifyRoster: async (from, to) => {
      try {
        const response = await apiClient.post('/people/shifts/assignments/notify', { from, to });
        const d = response.data?.data ?? response.data ?? {};
        return { notified: Number(d.notified ?? 0), emailed: Number(d.emailed ?? 0), employees: Number(d.employees ?? 0) };
      } catch (error: any) {
        throw new Error(messageOf(error, 'Could not send the schedule'));
      }
    },

    createShift: async (data) => {
      try {
        await apiClient.post('/people/shifts', data);
      } catch (error: any) {
        throw new Error(messageOf(error, 'Could not create the shift type'));
      }
      await get().fetchShifts();
    },

    updateShift: async (id, data) => {
      try {
        await apiClient.patch(`/people/shifts/${id}`, data);
      } catch (error: any) {
        throw new Error(messageOf(error, 'Could not update the shift type'));
      }
      await refresh();
    },

    deleteShift: async (id) => {
      try {
        await apiClient.delete(`/people/shifts/${id}`);
      } catch (error: any) {
        throw new Error(messageOf(error, 'Could not delete the shift type'));
      }
      set((state) => ({ shifts: state.shifts.filter((s) => s.id !== id) }));
    },
  };
});
